import { type OrderPreferencesState, SORT_OPTIONS } from '@choco-admin/types';
import { Card, CardBody } from '@heroui/react';
import React from 'react';
import { FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

import { BaseButton } from '../../../../../../components/BaseButton';
import { ChocoSelect } from '../../../../../../components/FormComponents';
import { useSupplierForm } from '../../SupplierFormProvider';
import { type TabProps } from '../../types';

import { PreferenceToggle } from './components';
import { DeliverySettingsSection } from './components/DeliverySettingsSection';
import { DEFAULT_FORM_VALUES } from './constants';

export const OrderPreferencesTab: React.FC<TabProps> = ({ mode, supplier }) => {
  const navigate = useNavigate();
  const handleBack = () => navigate(-1);

  const {
    formMethods: {
      watch,
      setValue,
      formState: { errors },
    },
  } = useSupplierForm();

  // In create mode or with supplier, always render with defaults
  return (
    <div className="space-y-6">
      {/* Back Button */}
      <BaseButton
        variant="light"
        startContent={<FiArrowLeft size={16} />}
        onClick={handleBack}
        className="mb-4"
      >
        Back
      </BaseButton>

      {/* Order Preferences */}
      <Card>
        <CardBody className="space-y-6">
          <h2 className="text-lg font-semibold">Order Preferences</h2>
          <div className="space-y-4">
            <PreferenceToggle
              label="Allow custom products"
              description="Let buyers add products that are not in your catalog"
              isSelected={
                watch('allowCustomProducts') ??
                DEFAULT_FORM_VALUES.allowCustomProducts
              }
              onChange={(value) => setValue('allowCustomProducts', value)}
            />

            <PreferenceToggle
              label="Mandatory customer number"
              description="Require buyers to provide a customer number when placing orders"
              isSelected={
                watch('mandatoryCustomerNumber') ??
                DEFAULT_FORM_VALUES.mandatoryCustomerNumber
              }
              onChange={(value) => setValue('mandatoryCustomerNumber', value)}
            />

            <PreferenceToggle
              label="Mandatory delivery date"
              description="Require buyers to select a delivery date when placing orders"
              isSelected={
                watch('mandatoryDeliveryDate') ??
                DEFAULT_FORM_VALUES.mandatoryDeliveryDate
              }
              onChange={(value) => setValue('mandatoryDeliveryDate', value)}
            />

            <PreferenceToggle
              label="Enable product-level comments"
              description="Allow buyers to add comments to individual products"
              isSelected={
                watch('enableProductComments') ??
                DEFAULT_FORM_VALUES.enableProductComments
              }
              onChange={(value) => setValue('enableProductComments', value)}
            />

            <PreferenceToggle
              label="Allow fractional units"
              description="Allow buyers to order partial units (e.g., 0.5 kg)"
              isSelected={
                watch('allowFractionalUnits') ??
                DEFAULT_FORM_VALUES.allowFractionalUnits
              }
              onChange={(value) => setValue('allowFractionalUnits', value)}
            />

            <PreferenceToggle
              label="Allow only one active variant"
              description="Restrict buyers to selecting only one variant per product"
              isSelected={
                watch('allowOnlyOneVariant') ??
                DEFAULT_FORM_VALUES.allowOnlyOneVariant
              }
              onChange={(value) => setValue('allowOnlyOneVariant', value)}
            />

            <div>
              <p className="font-medium mb-2">Buyer cart sort</p>
              <ChocoSelect
                value={watch('sortBy') ?? DEFAULT_FORM_VALUES.sortBy}
                onChange={(e) =>
                  setValue(
                    'sortBy',
                    e.target.value as OrderPreferencesState['sortBy']
                  )
                }
                options={SORT_OPTIONS}
                className="max-w-xs"
                aria-label="Buyer cart sort order"
                label="Sort order"
                errorMessage={errors.sortBy?.message}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Delivery Settings - Only show when supplier exists */}
      {supplier?.id && <DeliverySettingsSection supplierId={supplier.id} />}
    </div>
  );
};
